import React from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Compass, ArrowRight } from 'lucide-react';
import { useAuth } from '../../auth/hooks/auth';
import { useMenuContext } from './MenuContext';
import { MENU_GROUPS, GROUP_ICONS, AUTHOR_MENUS } from '../admin/layout/Sidebar';
import { ADMIN_GUIDE, AUTHOR_GUIDE, DASHBOARD_GUIDE, routeKey, GuideEntry } from './guideContent';

interface GuideCardProps {
  icon: React.ComponentType<any>;
  title: string;
  entry?: GuideEntry;
  to: string;
}

const GuideCard: React.FC<GuideCardProps> = ({ icon: Icon, title, entry, to }) => (
  <Link
    to={to}
    className="group flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-4 hover:border-[#088A61] hover:shadow-sm transition-all"
  >
    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#088A61]/10 flex items-center justify-center text-[#088A61]">
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#088A61] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
      </div>
      <p className="text-sm font-medium text-gray-700 mt-0.5">
        {entry?.summary ?? 'Open this module to get started.'}
      </p>
      {entry?.details && (
        <p className="text-sm text-gray-500 mt-1 leading-relaxed">{entry.details}</p>
      )}
    </div>
  </Link>
);

const ModuleGuide: React.FC = () => {
  const { user } = useAuth();
  const { adminMenus } = useMenuContext();

  const isAuthor = user?.role === 'author';

  // Group admin menus exactly the way the sidebar does, so the guide mirrors
  // what the user can actually see.
  const groupedMenus = MENU_GROUPS.reduce((acc, group) => {
    const items = adminMenus
      .filter(menu => menu.group === group)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    if (items.length > 0) acc[group] = items;
    return acc;
  }, {} as Record<string, typeof adminMenus>);

  const authorItems = AUTHOR_MENUS.filter(
    item => item.id === 'library' || (user?.menus || []).includes(item.id),
  );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Help &amp; Guide</h1>
        <p className="text-gray-500 text-sm sm:text-base">
          A summary of what each part of your workspace does. You're seeing only the modules
          available to {isAuthor ? 'you' : 'your role'} — open any one to jump straight to it.
        </p>
      </div>

      {isAuthor ? (
        <div className="space-y-4">
          <GuideCard
            icon={LayoutDashboard}
            title="Dashboard"
            entry={DASHBOARD_GUIDE}
            to="/author"
          />
          {authorItems.map(item => (
            <GuideCard
              key={item.id}
              icon={item.icon}
              title={item.label}
              entry={AUTHOR_GUIDE[item.id]}
              to={item.path}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {/* Dashboard always available */}
          <section>
            <div className="space-y-3">
              <GuideCard
                icon={LayoutDashboard}
                title="Dashboard"
                entry={DASHBOARD_GUIDE}
                to="/admin"
              />
            </div>
          </section>

          {Object.entries(groupedMenus).map(([group, items]) => {
            const GroupIcon = GROUP_ICONS[group] || Compass;
            return (
              <section key={group}>
                <div className="flex items-center gap-2 mb-3">
                  <GroupIcon className="w-4 h-4 text-gray-400" />
                  <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    {group}
                  </h2>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {items.map(item => (
                    <GuideCard
                      key={item.id}
                      icon={GroupIcon}
                      title={item.displayName}
                      entry={ADMIN_GUIDE[routeKey(item.route)]}
                      to={`/${item.route}`}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {Object.keys(groupedMenus).length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Compass className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Your modules will appear here once they've loaded.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModuleGuide;
